import { CommissionTransaction, PayoutSummary } from '@/types/commission-dashboard';
import { TransactionType, CommissionAdjustmentHeaderType } from '@/types/constants';

/**
 * Calculate payout summary from selected transactions and opening balances.
 *
 * IMPORTANT: This logic is duplicated in multiple places and must be kept in sync:
 * 1. Backend: CommissionStatementService.BuildStatementFromTransactions (source of truth for statement creation)
 * 2. Backend: CommissionPayoutService.GetPayoutHistoryAsync SQL query (history grid calculated fields)
 * 3. Frontend: This function (used by dashboard summary card, prepare screen, and summary screen)
 *
 * The categorization logic (draws, adjustments, perm placement, standard) and the formulas (netChange, netPayable, closingBalance) must match across all three.
 * Preferably consolidate to a single backend source of truth if possible.
 */
export function calculatePayoutSummary(
  selectedTransactions: CommissionTransaction[],
  openingBalances: Record<number, number> // userId (profileId) -> opening balance
): PayoutSummary {
  // Group transactions by user (using profileId)
  const transactionsByUser = new Map<number, CommissionTransaction[]>();
  
  selectedTransactions.forEach(transaction => {
    const userId = transaction.commissionUserProfileId;
    if (!transactionsByUser.has(userId)) {
      transactionsByUser.set(userId, []);
    }
    transactionsByUser.get(userId)!.push(transaction);
  });

  // Calculate per-user statements and aggregate
  let totalOpeningBalance = 0;
  let totalContractCommissions = 0;
  let totalPermPlacementCommissions = 0;
  let totalDraws = 0;
  let totalAdjustments = 0;
  let totalNetChange = 0;
  let totalNetPayable = 0;
  let totalClosingBalance = 0;

  transactionsByUser.forEach((userTransactions, userId) => {
    const openingBalance = openingBalances[userId] || 0;
    
    let contractCommissions = 0;
    let permPlacementCommissions = 0;
    let draws = 0;
    let adjustments = 0;

    userTransactions.forEach(transaction => {
      // Check if it's a draw
      if (transaction.commissionAdjustmentHeaderTypeCode === CommissionAdjustmentHeaderType.Draws) {
        draws += transaction.commissionAmount;
      }
      // Check if it's another type of adjustment
      else if (transaction.commissionAdjustmentHeaderTypeCode !== null) {
        adjustments += transaction.commissionAmount;
      }
      // Otherwise it's a regular commission
      else {
        // Determine if it's perm placement transaction
        const isPermanentPlacement = transaction.transactionTypeCode === TransactionType.PermanentPlacement;
        
        if (isPermanentPlacement) {
          permPlacementCommissions += transaction.commissionAmount;
        } else {
          contractCommissions += transaction.commissionAmount;
        }
      }
    });

    // Calculate derived values for this user
    const commissionEarned = permPlacementCommissions + contractCommissions;
    const netChange = openingBalance + commissionEarned - draws + adjustments;
    const netPayable = Math.max(netChange, 0) + draws;
    const closingBalance = Math.min(netChange, 0);

    // Aggregate to totals
    totalOpeningBalance += openingBalance;
    totalContractCommissions += contractCommissions;
    totalPermPlacementCommissions += permPlacementCommissions;
    totalDraws += draws;
    totalAdjustments += adjustments;
    totalNetChange += netChange;
    totalNetPayable += netPayable;
    totalClosingBalance += closingBalance;
  });

  return {
    openingBalance: totalOpeningBalance,
    contractCommissions: totalContractCommissions,
    permPlacementCommissions: totalPermPlacementCommissions,
    adjustments: totalAdjustments,
    draws: totalDraws,
    closingBalance: totalClosingBalance,
    netChange: totalNetChange,
    totalPayable: totalNetPayable,
    includesDrawsPayable: totalDraws
  };
}
